import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { config } from '@percent/workplace-giving/config/config'
import { useLogger, useWindowDimensions, useHdgwIframeParams } from '@percent/workplace-giving/common/hooks'
import { useColorTheme } from '@percent/workplace-giving/common/hooks/useColorTheme/useColorTheme'
import { Modal, Icon } from '@percent/lemonade'
import classes from '@percent/workplace-giving/common/components/Donation/DonationForm/DonationForm.module.scss'
import { DonationFormSkeletonLoader } from '@percent/workplace-giving/common/components/Donation/DonationForm/DonationFormSkeletonLoader/DonationFormSkeletonLoader'

export function AdyenRedirect() {
  const { search, pathname } = useLocation()
  const parentRoute = pathname.replace(/\/adyen-redirect$/, '') || '/'
  const { width } = useWindowDimensions()
  const { logError } = useLogger()
  const [showLoader, setShowLoader] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(true)
  const { i18n } = useTranslation()
  const { theme } = useColorTheme()
  const hdgwParams = useHdgwIframeParams({ locale: i18n.language, primaryColor: theme.primary400 })

  const iframeSrc = useMemo(() => {
    const params = new URLSearchParams(search)

    hdgwParams.forEach((value, key) => {
      params.set(key, value)
    })

    return `${config.urls.donationLinkBaseUrl}/donate/adyen-redirect?${params.toString()}`
  }, [search, hdgwParams])

  const handleClose = useCallback(() => {
    try {
      if (window.parent) {
        window.parent.postMessage({ message: 'donationWidgetClosed' }, '*')
      }
    } catch (e) {
      logError(e)
    }
    setIsModalOpen(false)
    window.history.replaceState(null, '', parentRoute)
  }, [logError, parentRoute])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!config.allowedMessageOrigins?.some(origin => ['*', event.origin].includes(origin))) return

      try {
        const eventData = event.data as { message: string }

        if (eventData.message === 'donationFormLoaded') {
          setShowLoader(false)
        }

        if (eventData.message === 'closeDonationWidget') {
          handleClose()
        }
      } catch (e) {
        logError(e)
      }
    }

    window.addEventListener('message', handleMessage)

    return () => window.removeEventListener('message', handleMessage)
  }, [handleClose, logError])

  return (
    <Modal
      borderRadiiSize="xlarge"
      open={isModalOpen}
      onClose={handleClose}
      width={400}
      isFullscreen={width < 450}
      showModalOverflow={false}
    >
      <div className={classes.closeButtonWrapper}>
        <button
          type="button"
          aria-label="Close"
          style={{ borderRadius: '2rem' }}
          onClick={handleClose}
          className={classes.closeButton}
        >
          <Icon name="close" size={4} color="gray600" />
        </button>
      </div>

      {showLoader ? (
        <div className={classes.container}>
          <DonationFormSkeletonLoader />
        </div>
      ) : null}

      <iframe
        title="donation-form"
        style={{
          border: 'none',
          borderRadius: '4px'
        }}
        src={iframeSrc}
        allow="payment *"
        width={width < 450 ? '100%' : '400px'}
        height="700px"
      />
    </Modal>
  )
}
